import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ElasticsearchSelect from '@client/components/ui/select/UniversalElasticsearchSelect';
import AggregationSelect from '@client/components/ui/select/AggregationSelect';
import RangeSelect from '@client/components/ui/select/RangeSelect';
import CultureTreeSelect from '@client/components/ui/select/CultureTreeSelect';

/**
 * Converts filterLabels from {valueId: label} format to [{value, label}] array format
 * that select components expect for initialLabels
 * @param {Object} labelsObject - Labels object with {valueId: label} structure
 * @returns {Array|null} Array of {value, label} objects or null if no labels
 */
const convertLabelsToArray = (labelsObject) => {
  if (!labelsObject || typeof labelsObject !== 'object') return null;
  const entries = Object.entries(labelsObject);
  if (entries.length === 0) return null;
  return entries.map(([value, label]) => ({ value, label }));
};

/**
 * Build the cross-field facet context passed to every ElasticsearchSelect.
 * Maps each active filter's selected values onto the ES field path that the
 * autocomplete endpoint understands. Range / boolean filters are skipped: they
 * live on numeric/boolean fields and would need a different query shape.
 */
const buildActiveFacets = (filters, configs) => {
  if (!filters || !Array.isArray(configs)) return {};
  const facets = {};
  configs.forEach((config) => {
    if (!config) return;
    // Only regular (aggregation) selects and tree selects participate.
    if (config.type && config.type !== 'tree_select') return;
    const raw = filters[config.key];
    if (raw === undefined || raw === null) return;
    const values = Array.isArray(raw) ? raw : [raw];
    const cleaned = values
      .filter((v) => v !== undefined && v !== null && v !== '')
      .map((v) => String(v));
    if (cleaned.length === 0) return;
    const facetField = config.value_field
      ? `${config.field}.${config.value_field}`
      : config.field;
    facets[facetField] = cleaned;
  });
  return facets;
};

/**
 * Reusable filter select component
 * @param {Object} config - Filter configuration
 * @param {Object} filters - Current filter values
 * @param {Function} onFilterChange - Filter change handler
 * @param {string} document - Elasticsearch document type
 * @param {Object} aggregations - Aggregation data from API response
 * @param {Object} filterLabels - Labels for cold start restoration
 * @param {Object} activeFacets - Cross-field facet selections ({fieldPath: [values]})
 * @param useAggregations
 * @returns {JSX.Element}
 */
const FilterSelect = ({
  config,
  filters,
  onFilterChange,
  document,
  aggregations,
  filterLabels,
  activeFacets,
}) => {
  const { t } = useTranslation();
  const languageConfig = config.languageConfig || null;

  const aggregationOptions = aggregations?.getOptions
    ? aggregations.getOptions(config.key)
    : [];
  const hasAggregationData =
    aggregations && aggregationOptions && aggregationOptions.length > 0;

  // Convert filterLabels from {valueId: label} to [{value, label}] format
  const initialLabelsArray = useMemo(() => {
    return convertLabelsToArray(filterLabels?.[config.key]);
  }, [filterLabels, config.key]);

  // Extract query params from endpoint for tree_select filters
  const treeFilterParams = useMemo(() => {
    if (config.type !== 'tree_select' || !config.endpoint) return {};
    try {
      const url = new URL(config.endpoint, window.location.origin);
      const params = {};
      url.searchParams.forEach((val, key) => { params[key] = val; });
      return Object.keys(params).length > 0 ? params : {};
    } catch {
      return {};
    }
  }, [config.type, config.endpoint]);

  if (!config.type && config?.aggregate) {
    return (
      <AggregationSelect
        filterKey={config.key}
        options={aggregationOptions}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          onFilterChange(config.key, values, selectedItems);
        }}
        label={config.labelKey}
        showCount={true}
        initialLabels={initialLabelsArray}
      />
    );
  }

  if (config.type === 'boolean_select') {
    const boolLabels = filterLabels?.[config.key];
    const boolLabelTransform = (label, value) => {
      const key = String(value);
      return boolLabels?.[key] ?? boolLabels?.[value] ?? ({ true: t('common|Yes'), false: t('common|No') })[key] ?? label;
    };
    return (
      <ElasticsearchSelect
        document={document}
        label={config.labelKey}
        field={config.field}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          const transformedItems = selectedItems?.map((item) => ({
            ...item,
            label: boolLabelTransform(item.label, item.value),
          }));
          onFilterChange(config.key, values, transformedItems);
        }}
        mode="multiple"
        allowClear
        maxTagCount={1}
        pageSize={20}
        sortBy="natural"
        sortOrder="asc"
        showCount={false}
        infinite={true}
        style={{ width: '100%' }}
        initialLabels={initialLabelsArray}
        labelTransform={boolLabelTransform}
      />
    );
  }

  if (config.type === 'range_select') {
    return (
      <RangeSelect
        label={config.labelKey}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          onFilterChange(config.key, values, selectedItems);
        }}
        mode="multiple"
        allowClear
        maxTagCount={1}
        style={{ width: '100%' }}
      />
    );
  }

  if (config.type === 'tree_select') {
    return (
      <CultureTreeSelect
        label={config.labelKey}
        endpoint={config.endpoint}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) =>
          onFilterChange(config.key, values, selectedItems)
        }
        placeholder={t('common|Search...')}
        isMulti={true}
        labelField={config.label_field || 'french_name'}
        valueField={config.value_field || 'id'}
        languageFields={languageConfig?.fields}
        languageLabels={languageConfig?.labels}
        defaultLanguage={languageConfig?.defaultLanguage || 'fr'}
        showLanguageSelector={!!languageConfig}
        style={{ width: '100%' }}
        enrichedCultures={initialLabelsArray}
        filterParams={treeFilterParams}
        maxTagCount={1}
      />
    );
  }

  // When filterLabels has a pre-resolved label for this filter's bucket
  // value (e.g. risk-level mappings pre-populated in useFilterLabels),
  // prefer that over the raw backend label. Handles both numeric and string
  // keys so URL-state values ("0") and ES bucket keys (0) both resolve.
  const perKeyLabels = filterLabels?.[config.key];
  const labelTransform = perKeyLabels
    ? (label, value) =>
        perKeyLabels[value] ?? perKeyLabels[String(value)] ?? label
    : null;

  return (
    <ElasticsearchSelect
      document={document}
      label={config.labelKey}
      field={config.field}
      label_field={config.label_field || null}
      value_field={config.value_field || null}
      value={filters[config.key] || []}
      onChange={(values, selectedItems) =>
        onFilterChange(config.key, values, selectedItems)
      }
      mode="multiple"
      allowClear
      maxTagCount={1}
      pageSize={20}
      sortBy={config.sortBy || 'natural'}
      sortOrder={config.sortOrder || 'asc'}
      showCount={false}
      infinite={true}
      style={{ width: '100%' }}
      languageFields={languageConfig?.fields}
      languageLabels={languageConfig?.labels}
      defaultLanguage={languageConfig?.defaultLanguage || 'fr'}
      showLanguageSelector={!!languageConfig}
      initialLabels={initialLabelsArray}
      filters={config.filters || null}
      apiEndpoint={config.apiEndpoint || null}
      apiFilters={config.apiFilters || null}
      activeFacets={activeFacets}
      labelTransform={labelTransform}
      searchFields={config.searchFields || null}
      includeSourceFields={config.includeSourceFields || null}
      formatOptionLabel={config.formatOptionLabel || null}
      infoBulle={config.infoBulle || null}
      tutorialSlotKey={config.tutorialSlotKey || null}
    />
  );
};

/**
 * Reusable filter section component
 * @param {Object} props
 * @param {Array} props.filterConfigs - Array of filter configurations rendered in this section
 * @param {Array} [props.allFilterConfigs] - Full union of filter configs across all tabs.
 *   Used to compute cross-field facets so a selection in one tab narrows
 *   options in another. Falls back to `filterConfigs` if omitted.
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {string} props.document - Elasticsearch document type
 * @param {string} props.className - Optional CSS class
 * @param {Object} props.aggregations - Aggregation data from API response
 * @param {Object} props.filterLabels - Labels for cold start restoration
 * @returns {JSX.Element}
 */
const FilterSection = ({
  filterConfigs,
  allFilterConfigs,
  filters,
  onFilterChange,
  document,
  className = 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  aggregations,
  filterLabels,
}) => {
  const activeFacets = useMemo(
    () => buildActiveFacets(filters, allFilterConfigs || filterConfigs),
    [filters, allFilterConfigs, filterConfigs]
  );

  return (
    <div className={className}>
      {filterConfigs.map((config) => (
        <div key={config.key}>
          <FilterSelect
            config={config}
            filters={filters}
            onFilterChange={onFilterChange}
            document={document}
            aggregations={aggregations}
            filterLabels={filterLabels}
            activeFacets={activeFacets}
          />
        </div>
      ))}
    </div>
  );
};

export default FilterSection;
